import { useStorage } from "@vueuse/core";
import { logError } from "~vue/utils/logError";
import { isPagePublic, reportSessionEnded, SESSION_EXPIRED } from "~vue/utils/sessionEnded";
import { DateTime } from "luxon";
import { computed, readonly, ref } from "vue";

import { convertToDateTime } from "../../dateUtils.js";
import { getCookie } from "../../utils.js";

type TokenResponse = {
    token: string;
    expires_at: Date;
};

const RETRY_LIMIT = 2;
const REFETCH_BEFORE_EXPIRY_MS = 10000; // 10 secs

const apiTokenStorageObj = useStorage<Partial<TokenResponse>>("valence_nadia_api_token", {});

const apiToken = computed(() => apiTokenStorageObj.value.token);
// const apiTokenExpiry = computed(() => apiTokenStorageObj.value.expires_at);
const isFetchingToken = ref(false);
const tokenPromise = ref<Promise<void> | null>(null);
let _tokenRefetchTimeout: ReturnType<typeof setTimeout> | null = null;

// When set, the token module uses the feedback token endpoint instead of the regular one.
let _feedbackCoachingSessionUuid: string | null = null;

/**
 * Configure the token module to use the anonymous feedback token endpoint.
 * Call once on the feedback page before any token fetch occurs.
 */
const setFeedbackSession = (coachingSessionUuid: string) => {
    _feedbackCoachingSessionUuid = coachingSessionUuid;
};

/**
 * Clear the feedback session state so subsequent token fetches
 * use the regular authenticated endpoint.
 */
const clearFeedbackSession = () => {
    _feedbackCoachingSessionUuid = null;
};

/**
 * Returns whether the token module is configured for the feedback flow.
 */
const isFeedbackSession = (): boolean => _feedbackCoachingSessionUuid !== null;

/**
 * Whether this response means the Django session backing the token endpoint is gone.
 *
 * Never true for the feedback flow: those raters are anonymous by design, so a rejection there
 * is an invalid or expired invite rather than a session the user could log back into.
 */
const _isSessionEnded = (response: Response): boolean => !_feedbackCoachingSessionUuid && (response.status === 401 || response.status === 403);

const _getTokenEndpoint = (): string => {
    if (_feedbackCoachingSessionUuid) {
        return `/feedback/token/${_feedbackCoachingSessionUuid}`;
    }
    return "/api/accounts/token";
};

/**
 * creates a timeout to refetch the access token before it expires
 * will also handle an already expired token refetch since the timeout duration will be 0
 * @param expiresAt date time when this access token is set to expire
 */
const _setTokenRefetch = (expiresAt: Date) => {
    const expirationDateTime = convertToDateTime(expiresAt);
    const expirationDuration = expirationDateTime.diff(DateTime.now()).milliseconds;
    const timeoutDuration = Math.max(0, expirationDuration - REFETCH_BEFORE_EXPIRY_MS);

    if (_tokenRefetchTimeout !== null) {
        clearTimeout(_tokenRefetchTimeout);
        _tokenRefetchTimeout = null;
    }

    _tokenRefetchTimeout = setTimeout(() => {
        tokenPromise.value = getToken();
    }, timeoutDuration);
};

const _getToken = async () => {
    let retries = 0;
    let expectedAnonymousRejection = false;

    const csrfCookie = getCookie("csrftoken");
    if (csrfCookie == null) {
        throw new Error("CSRF token cookie not found");
    }

    // uses fetch here since useFetch is not yet defined in this block scope
    isFetchingToken.value = true;
    let response: Response;
    try {
        do {
            response = await fetch(_getTokenEndpoint(), {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfCookie,
                },
            });
            // Retrying an auth failure can only fail again, and each attempt delays the dialog.
        } while (!response.ok && !_isSessionEnded(response) && retries++ < RETRY_LIMIT);

        if (!response.ok) {
            // This endpoint authenticates by session, so losing it is indistinguishable from
            // never having had it. Without this the failure was invisible: the rejection below
            // makes useFetch's beforeFetch cancel() the caller's request, so the user clicked
            // and simply nothing happened (BUG-947).
            if (_isSessionEnded(response)) {
                reportSessionEnded(SESSION_EXPIRED);
                expectedAnonymousRejection = isPagePublic();
            }
            isFetchingToken.value = false;
            throw new Error(`Failed to fetch token after ${RETRY_LIMIT + 1} attempts`);
        }
        const data: TokenResponse = await response.json();
        apiTokenStorageObj.value = data;
        _setTokenRefetch(data.expires_at);
        tokenPromise.value = null;
        isFetchingToken.value = false;
    } catch (error) {
        // catches the reject scenarios for fetch, e.g. network errors, bad scheme, and exhausted retries.
        // A public page has no session to mint a token from, so a rejection there is its normal
        // case and not worth alerting on.
        if (!expectedAnonymousRejection) {
            logError(error instanceof Error ? error : new Error("An unknown error occurred while fetching the token"));
        }
        return Promise.reject(error);
    }
};

const getToken = async (): Promise<void> => {
    tokenPromise.value = _getToken();
    await tokenPromise.value;
};

// commented out for now and will lazily fetch the token when the specific version endpoint is hit, from useFetch
// if (!apiToken.value && !isFetchingToken.value) {
//     // fetch token immediately when this module is imported if there is no token yet
//     tokenPromise.value = getToken();
// } else if (apiTokenExpiry.value && _tokenRefetchTimeout == null) {
//     _setTokenRefetch(apiTokenExpiry.value);
// }

export default {
    getToken,
    setFeedbackSession,
    clearFeedbackSession,
    isFeedbackSession,
    isFetchingToken: readonly(isFetchingToken),
    tokenPromise: readonly(tokenPromise),
    apiToken: readonly(apiToken),
};
